import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import { LanguageTabs } from "./LanguageTabs";
import { checkSkuAvailability } from "@/lib/api";

// The variant row in the DB always needs some img_url (NOT NULL). This
// placeholder is what a brand-new variant is created with, before its real
// picture (if any) is uploaded via POST /image/{variant_id}. It never
// resolves to a real signed URL, so the UI shows the fallback for it.
export const PENDING_IMAGE_PLACEHOLDER = "pending";

export type VariantDraft = {
  sku: string;
  color: string;
  color_en: string;
  flavour: string;
  flavour_en: string;
  size: string;
  size_en: string;
  // A newly picked, not-yet-uploaded file. Null means "no change".
  imageFile: File | null;
  // The signed URL of the image currently saved for this variant (if any).
  // Only meaningful for a variant that already exists.
  existingImageUrl: string | null;
  price: string;
};

export const emptyVariant: VariantDraft = {
  sku: "",
  color: "",
  color_en: "",
  flavour: "",
  flavour_en: "",
  size: "",
  size_en: "",
  imageFile: null,
  existingImageUrl: null,
  price: "",
};

export type VariantPayload = {
  sku: string;
  color: string | null;
  color_en: string | null;
  flavour: string | null;
  flavour_en: string | null;
  size: string | null;
  size_en: string | null;
  price: number;
};

// Deliberately does not include img_url: image uploads are a separate step
// (POST /image/{variant_id}), never part of the variant's own create/update
// payload here.
export function variantDraftToPayload(v: VariantDraft): VariantPayload {
  return {
    sku: v.sku,
    color: v.color || null,
    color_en: v.color_en || null,
    flavour: v.flavour || null,
    flavour_en: v.flavour_en || null,
    size: v.size || null,
    size_en: v.size_en || null,
    price: Number(v.price),
  };
}

type VariantDraftSource = {
  sku?: string | null;
  color?: string | null;
  color_en?: string | null;
  flavour?: string | null;
  flavour_en?: string | null;
  size?: string | null;
  size_en?: string | null;
  price?: number | null;
};

export function variantToDraft(v: VariantDraftSource, existingImageUrl: string | null = null): VariantDraft {
  return {
    sku: v.sku ?? "",
    color: v.color ?? "",
    color_en: v.color_en ?? "",
    flavour: v.flavour ?? "",
    flavour_en: v.flavour_en ?? "",
    size: v.size ?? "",
    size_en: v.size_en ?? "",
    imageFile: null,
    existingImageUrl,
    price: v.price !== undefined ? String(v.price) : "",
  };
}

// English fields (color_en/flavour_en/size_en) are purely optional
// translations - only sku, price, and one of color/flavour/size (in
// whichever language) plus a picture (new file or already saved) are ever
// required.
export function validateVariantDraft(v: VariantDraft): string | null {
  if (!v.sku.trim()) return "skuRequired";
  if (!v.price || Number(v.price) <= 0) return "priceGreaterThanZero";
  if (!v.color.trim() && !v.flavour.trim() && !v.size.trim() && !v.color_en.trim() && !v.flavour_en.trim() && !v.size_en.trim()) {
    return "atLeastOneOf";
  }
  if (!v.imageFile && !v.existingImageUrl) return "imageRequired";
  return null;
}

type VariantFieldsProps = {
  value: VariantDraft;
  onChange: (value: VariantDraft) => void;
  disabled?: boolean;
  // The id of the variant currently being edited, so its own SKU isn't
  // flagged as taken. Omitted when adding a brand-new variant.
  variantId?: number;
};

const SKU_CHECK_DEBOUNCE_MS = 400;

export function VariantFields({ value, onChange, disabled, variantId }: VariantFieldsProps) {
  const { t } = useTranslation();
  const [activeLanguage, setActiveLanguage] = useState<"es" | "en">("es");
  const isSpanish = activeLanguage === "es";
  const [skuStatus, setSkuStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");

  const skuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skuAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (skuTimerRef.current) clearTimeout(skuTimerRef.current);
    skuAbortRef.current?.abort();

    const sku = value.sku.trim();
    if (!sku) {
      setSkuStatus("idle");
      return;
    }

    setSkuStatus("checking");
    skuTimerRef.current = setTimeout(() => {
      const controller = new AbortController();
      skuAbortRef.current = controller;
      checkSkuAvailability(sku, variantId, controller.signal)
        .then(available => setSkuStatus(available ? "available" : "taken"))
        .catch(() => {
          if (!controller.signal.aborted) setSkuStatus("idle");
        });
    }, SKU_CHECK_DEBOUNCE_MS);

    return () => {
      if (skuTimerRef.current) clearTimeout(skuTimerRef.current);
      skuAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.sku, variantId]);

  const set =
    (key: keyof VariantDraft) => (e: ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, [key]: e.target.value });

  const objectUrl = useMemo(
    () => (value.imageFile ? URL.createObjectURL(value.imageFile) : null),
    [value.imageFile]
  );
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);
  const previewSrc = objectUrl ?? value.existingImageUrl;

  return (
    <div className="flex flex-col gap-3 w-full">
      <span className="text-xs font-semibold text-brand uppercase tracking-wide">{t("variant.fieldsLabel")}</span>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-foreground">{t("variant.sku")}</label>
        <input
          className="input bg-app-control border-app-border text-foreground w-full"
          value={value.sku}
          onChange={set("sku")}
          disabled={disabled}
        />
        {skuStatus === "checking" && (
          <span className="text-xs text-app-muted">{t("variant.skuChecking")}</span>
        )}
        {skuStatus === "taken" && (
          <span className="text-xs text-red-500">{t("variant.skuTaken")}</span>
        )}
      </div>

      <LanguageTabs active={activeLanguage} onChange={setActiveLanguage} className="self-end" />

      <div className="grid grid-cols-3 gap-2">
        {isSpanish ? (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t("variant.color")}</label>
              <input className="input bg-app-control border-app-border text-foreground w-full" value={value.color} onChange={set("color")} disabled={disabled} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t("variant.flavour")}</label>
              <input className="input bg-app-control border-app-border text-foreground w-full" value={value.flavour} onChange={set("flavour")} disabled={disabled} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t("variant.size")}</label>
              <input className="input bg-app-control border-app-border text-foreground w-full" value={value.size} onChange={set("size")} disabled={disabled} />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t("variant.colorEn")}</label>
              <input className="input bg-app-control border-app-border text-foreground w-full" value={value.color_en} onChange={set("color_en")} disabled={disabled} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t("variant.flavourEn")}</label>
              <input className="input bg-app-control border-app-border text-foreground w-full" value={value.flavour_en} onChange={set("flavour_en")} disabled={disabled} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t("variant.sizeEn")}</label>
              <input className="input bg-app-control border-app-border text-foreground w-full" value={value.size_en} onChange={set("size_en")} disabled={disabled} />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-foreground">{t("variant.price")}</label>
        <input
          type="number"
          step="0.01"
          className="input bg-app-control border-app-border text-foreground w-full"
          value={value.price}
          onChange={set("price")}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col items-center gap-2 pt-1">
        <label className="text-sm font-medium text-foreground self-start">{t("variant.image")}</label>
        {previewSrc && (
          <img
            src={previewSrc}
            alt=""
            className="w-20 h-20 object-contain bg-white border border-app-border rounded-lg"
          />
        )}
        <label
          className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border-2 border-dashed text-sm font-medium transition-colors duration-200 ${
            disabled
              ? "border-app-border text-app-muted cursor-not-allowed"
              : "border-app-border text-app-muted hover:border-brand hover:text-brand cursor-pointer"
          }`}
        >
          <Upload size={16} />
          {previewSrc ? t("variant.changeImage") : t("variant.chooseImage")}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={e => onChange({ ...value, imageFile: e.target.files?.[0] ?? null })}
            disabled={disabled}
          />
        </label>
      </div>
    </div>
  );
}
